import React, { useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import { graphql } from 'gatsby'
import { BaseCSS, GridThemeProvider } from 'styled-bootstrap-grid'
import { css, ThemeProvider } from 'styled-components'

import { ResetCSS, GlobalCSS, initUiLibrary } from 'mf-ui-components'

import Layout from '../components/Layout'
import { SEO } from '../components/SEO'
import PageBlocks from '../components/PageBlocks'
import { Helmet } from 'react-helmet'
import gridTheme from '../utils/gridTheme'
import { intlNumberformatPolyfill } from '../utils/intlNumberformatPolyfill'
import { i18n } from '../utils/i18n'
import { UnsupportedBrowserScript } from '../utils/scripts/UnsupportedBrowser'
import { WEBSITE_MAX_WIDTH } from '../utils/siteMaxWidth'
import { getTheme } from '../utils/style/getTheme'

import 'react-loading-skeleton/dist/skeleton.css'

const Page = (props) => {
  useEffect(() => {
    /**
     * InitUILibrary makes some API calls to obtain the SVGs, however the server is not up at build time so we cannot obtain them...
     */
    initUiLibrary()
    intlNumberformatPolyfill()
  }, [])

  const {
    pageContext: { mediaItems, isHomepage, generalOptions },
  } = props

  const {
    data: {
      wpgraphql: { page },
    },
  } = props
  const { blocks, seo, scripts } = page
  const {
    title,
    twitterDescription,
    twitterImage,
    twitterTitle,
    opengraphTitle,
    opengraphImage,
    opengraphDescription,
    metaRobotsNoindex,
    metaRobotsNofollow,
    metaKeywords,
    metaDesc,
    canonical,
  } = seo

  const theme = getTheme(generalOptions.theme)
  const isABTestingEnabled = !!scripts.scripts.maxymiser

  return (
    <GridThemeProvider gridTheme={gridTheme}>
      <ThemeProvider theme={theme}>
        <IntlProvider messages={i18n()} locale={process.env.GATSBY_LANG}>
          <ResetCSS />
          <GlobalCSS />
          <BaseCSS />
          <style>
            {css`
              body {
                width: 100%;
                margin: 0 auto;
                overflow-x: hidden;
              }

              @media screen and (min-width: ${WEBSITE_MAX_WIDTH}) and (orientation: landscape) {
                body {
                  max-width: ${WEBSITE_MAX_WIDTH};
                  border-left: 1px solid
                    ${theme.tokens.interface.neutral.strong};
                  border-right: 1px solid
                    ${theme.tokens.interface.neutral.strong};
                }
              }
            `}
          </style>

          <Layout slug={page.slug} themeId={theme.id}>
            <SEO
              title={title}
              description={metaDesc}
              keywords={metaKeywords}
              canonical={isHomepage ? undefined : canonical}
              opengraphTitle={opengraphTitle}
              opengraphDescription={opengraphDescription}
              opengraphImage={opengraphImage}
              twitterTitle={twitterTitle}
              twitterDescription={twitterDescription}
              twitterImage={twitterImage}
              metaRobotsNoindex={metaRobotsNoindex}
              metaRobotsNofollow={metaRobotsNofollow}
            />
            <Helmet>
              {theme.id === 'mf-2023-light' && (
                <link
                  rel="stylesheet"
                  href="https://use.typekit.net/ovn1pte.css"
                  // This is a trick to asynchronously load the font css file,
                  // in order to not block the rendering of the page.
                  media="print"
                  onload="this.media='all'; this.onload=null;"
                />
              )}
              {isABTestingEnabled && (
                <link
                  rel="preconnect"
                  href="https://dev.visualwebsiteoptimizer.com"
                />
              )}
              {isABTestingEnabled && (
                <script type="text/javascript" id="vwoCode">
                  {`
                  window._vwo_code || (function() {
                  var account_id=42045,
                  version=2.1,
                  settings_tolerance=2000,
                  hide_element='body',
                  hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;transition:none !important;',
                  /* DO NOT EDIT BELOW THIS LINE */
                  f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement('script'),r=this;t=t||{};if(i){n.textContent=i;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200||o.status===304){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var i=this.hide_element(),n=i?i+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var n=d.createElement('div');n.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';n.setAttribute('id','_vis_opt_path_hides');n.classList.add('_vis_hide_layer');t.parentNode.insertBefore(n,t.nextSibling)}var o='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:o})}else{this.load(o+'&x=true')}}};w._vwo_code=code;code.init();})();
                  `}
                </script>
              )}

              <script type="speculationrules">
                {`{
                    "prefetch": [{
                      "source": "document",
                      "where": { "selector_matches": "a[data-preload]" },
                      "eagerness": "eager"
                    }],
                    "prerender":[{
                      "source": "document",
                      "where": { "selector_matches": "a[data-preload]" },
                      "eagerness": "moderate"
                    }] 
                  }`}
              </script>
              <UnsupportedBrowserScript theme={theme} />
            </Helmet>
            <PageBlocks mediaItems={mediaItems} blocks={blocks} />
          </Layout>
        </IntlProvider>
      </ThemeProvider>
    </GridThemeProvider>
  )
}

export default Page

export const pageQuery = graphql`
  query GET_PAGE($id: ID!) {
    wpgraphql {
      page(id: $id) {
        slug
        link
        seo {
          title
          twitterDescription
          twitterImage {
            mediaItemUrl
          }
          twitterTitle
          opengraphTitle
          opengraphImage {
            mediaItemUrl
          }
          opengraphDescription
          metaRobotsNoindex
          metaRobotsNofollow
          metaKeywords
          metaDesc
          canonical
        }
        scripts {
          scripts {
            maxymiser
          }
        }
        blocks {
          __typename

          ... on WPGraphQL_MfblocksHeroBlock {
            ...heroFields
          }
          ... on WPGraphQL_MfblocksBsColumnsBlock {
            ...columnsFields
          }
          ... on WPGraphQL_MfblocksParagraphBlock {
            ...paragraphFields
          }
          ... on WPGraphQL_MfblocksHeadingBlock {
            ...headingFields
          }
          ... on WPGraphQL_MfblocksTextLinkBlock {
            ...textLinkFields
          }
          ... on WPGraphQL_MfblocksButtonBlock {
            ...buttonFields
          }
          ... on WPGraphQL_MfblocksStripeBlock {
            ...stripeFields
          }
          ... on WPGraphQL_CoreEmbedYoutubeBlock {
            ...youtubeFields
          }
          ... on WPGraphQL_MfblocksIconFeatureBlock {
            ...iconFeatureFields
          }
          ... on WPGraphQL_MfblocksFeatureCardBlock {
            ...featureCardFields
          }
          ... on WPGraphQL_MfblocksImageBlock {
            ...imageFields
          }
          ... on WPGraphQL_MfblocksBlockquoteBlock {
            ...blockquoteFields
          }
          ... on WPGraphQL_MfblocksTimelineBlock {
            ...timelineFields
          }
          ... on WPGraphQL_MfblocksTableBlock {
            ...tableFields
          }
          ... on WPGraphQL_MfblocksListBlock {
            ...listFields
          }
          ... on WPGraphQL_MfblocksOnPageNavBlock {
            ...pageNavFields
          }
          ... on WPGraphQL_MfblocksAccordionBlock {
            ...accordionFields
          }
          ... on WPGraphQL_MfblocksNoticeBlock {
            ...noticeFields
          }
          ... on WPGraphQL_MfblocksPricingBlock {
            ...pricingFields
          }
          ... on WPGraphQL_MfblocksPressItemBlock {
            ...pressItemFields
          }
          ... on WPGraphQL_CoreHtmlBlock {
            ...htmlFields
          }
          ... on WPGraphQL_MfblocksFeatureBoxBlock {
            ...featureBoxFields
          }
          ... on WPGraphQL_MfblocksFaqBlock {
            ...faqFields
          }
          ... on WPGraphQL_MfblocksMapBlock {
            ...mapFields
          }
          ... on WPGraphQL_MfblocksCallFormBlock {
            ...callFields
          }
          ... on WPGraphQL_MfblocksTeamBlock {
            ...teamFields
          }
          ... on WPGraphQL_MfblocksPensionCalcBlock {
            ...pensionCalcFields
          }
          ... on WPGraphQL_MfblocksTabsBlock {
            ...tabsFields
          }
          ... on WPGraphQL_MfblocksHrBlock {
            ...hrFields
          }
          ... on WPGraphQL_MfblocksNestedColumnsBlock {
            ...nestedColumnsFields
          }
          ... on WPGraphQL_MfblocksTrustpilotBlock {
            ...trustpilotFields
          }
          ... on WPGraphQL_MfblocksGlossaryBlock {
            ...glossaryFields
          }
          ... on WPGraphQL_MfblocksModelPortfoliosBlock {
            ...modelPortfoliosFields
          }
          ... on WPGraphQL_MfblocksAssetsBreakdownBlock {
            ...assetsBreakdownFields
          }
          ... on WPGraphQL_MfblocksInvestorProfileTabsBlock {
            ...investorProfileTabsFields
          }
          ... on WPGraphQL_MfblocksLottieAnimationBlock {
            ...lottieAnimationFields
          }
          ... on WPGraphQL_MfblocksExpectedPerformanceBlock {
            ...expectedPerformanceFields
          }
          ... on WPGraphQL_MfblocksPricingCardsListBlock {
            ...pricingCardsListFields
          }
          ... on WPGraphQL_MfblocksLightCarouselBlock {
            ...lightCarouselFields
          }
          ... on WPGraphQL_MfblocksBlogEntriesBlock {
            ...blogEntriesFields
          }
          ... on WPGraphQL_MfblocksMultiProductPricingCalculatorBlock {
            ...multiProductPricingCalculatorFields
          }
          ... on WPGraphQL_MfblocksImagesCarouselBlock {
            ...imagesCarouselFields
          }
          ... on WPGraphQL_MfblocksAppDownloadButtonBlock {
            ...appDownloadButtonFields
          }
          ... on WPGraphQL_MfblocksCrosswordBlock {
            ...crosswordFields
          }
        }
      }
    }
  }
`
